import React from "react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader, CTASection } from "@/components/Sections";
import { Reveal, TechLabel } from "@/components/Reveal";
import { INDUSTRIES } from "@/lib/site";

export default function Industries() {
  useSEO({
    title: "Mining Equipment Parts & Components | BlackSilex",
    description: "Mining equipment parts and components for the operations BlackSilex serves — underground mining, surface mining and mineral processing across Ontario, Canada.",
    path: "/industries",
  });

  return (
    <div data-testid="industries-page">
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "Mining" }]}
        eyebrow="Industries / Mining"
        titleLines={["Mining."]}
      />

      <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="border-t border-l border-offwhite/10 grid md:grid-cols-3">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.id} delay={i * 0.1}>
              <div id={ind.id} className="border-r border-b border-offwhite/10 p-8 lg:p-12 h-full scroll-mt-24" data-testid={`industry-${ind.id}`}>
                {ind.img && (
                  <div className="mb-8 -mx-8 -mt-8 lg:-mx-12 lg:-mt-12 overflow-hidden border-b border-offwhite/10">
                    <img src={ind.img} alt={ind.title} loading="lazy" decoding="async" className="w-full h-52 object-cover" data-testid={`industry-img-${ind.id}`} />
                  </div>
                )}
                <TechLabel>{ind.tag}</TechLabel>
                <h2 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl leading-[0.95] mt-6">{ind.title}</h2>
                {ind.tree ? (
                  <div className="mt-8" data-testid={`industry-tree-${ind.id}`}>
                    <div className="flex items-center gap-3">
                      <span className="w-2.5 h-2.5 bg-strike shrink-0" />
                      <span className="font-display text-lg font-bold uppercase tracking-tight text-offwhite">{ind.tree.root}</span>
                    </div>
                    <div className="ml-[4px] mt-1 border-l border-offwhite/25 pl-7 space-y-5 pt-1">
                      {ind.tree.branches.map((b) => (
                        <div key={b} className="relative flex items-center gap-3">
                          <span className="absolute -left-7 top-1/2 w-7 h-px bg-offwhite/25" />
                          <span className="w-2 h-2 border border-strike shrink-0" />
                          <span className="text-mutedgray text-base leading-relaxed">{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : ind.copy ? (
                  <p className="mt-8 text-mutedgray text-base leading-relaxed" data-testid={`industry-copy-${ind.id}`}>{ind.copy}</p>
                ) : (
                <ul className="mt-8 space-y-3">
                  {ind.items.map((it) => (
                    <li key={it.text}>
                      <div className="flex items-start gap-3 text-mutedgray text-base leading-relaxed">
                        <span className="w-1.5 h-1.5 bg-strike mt-2 shrink-0" />
                        <span>{it.text}</span>
                      </div>
                      {it.sub && (
                        <ul className="mt-2 ml-5 space-y-1.5">
                          {it.sub.map((s) => (
                            <li key={s} className="flex items-start gap-3 text-mutedgray/90 text-base leading-relaxed">
                              <span className="w-1 h-1 border border-strike mt-2.5 shrink-0" />
                              <span>{s}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection />
    </div>
  );
}
